import React, { useEffect, useState } from 'react';

/**
 * PROTOTYPE -- the painting swinging open on a hinge to show the Vault door.
 *
 * Built from the one Room image we have, so it can be felt on a real phone
 * before the proper layers are commissioned. It is deliberately temporary:
 *
 *   - the lid is the painting cut out of the background image itself (same
 *     picture, same scrim, same crop), laid exactly over the painted one;
 *   - behind it, a dark recess and a Vault door drawn in CSS, because the
 *     real wall behind the painting does not exist yet;
 *   - the swing is a flat CSS rotation of a flat photo. The blueprint says
 *     plainly that this is a prototype, not the finished illusion.
 *
 * To be replaced by the clean layers (wall with vault door, cut-out painting
 * with frame edge and shadow, the picture lamp's light) once they exist and
 * pass review. Nothing else depends on this file.
 *
 * `box` is [x0, x1, y0, y1] in fractions of the drawn image; `art` and
 * `scrim` must be the background's own, so the lid matches it pixel for pixel.
 */

// The lid's swing, then a short hold on the open door before the Vault.
export const LID_SWING_MS = 700;
export const LID_HOLD_MS = 180;

const pct = (v) => Math.round(v * 100000) / 1000;

const VaultDoor = () => (
  // A round door: rim, inner ring, three spokes crossing at the hub.
  <div className="absolute inset-0 flex items-center justify-center" aria-hidden="true">
    <div
      className="relative rounded-full"
      style={{
        width: '72%',
        aspectRatio: '1 / 1',
        border: '3px solid hsl(var(--gold) / 0.85)',
        background: 'radial-gradient(circle at 40% 35%, #2a2418 0%, #14110c 60%, #0a0907 100%)',
        boxShadow: '0 0 0 2px #07090A, 0 0 18px hsl(var(--gold) / 0.25), inset 0 0 22px #000',
      }}
    >
      <div className="absolute rounded-full" style={{ inset: '16%', border: '1.5px solid hsl(var(--gold) / 0.55)' }} />
      {[0, 60, 120].map((deg) => (
        <div
          key={deg}
          className="absolute left-1/2 top-1/2"
          style={{
            width: '70%', height: '2px', background: 'hsl(var(--gold) / 0.7)',
            transform: `translate(-50%, -50%) rotate(${deg}deg)`,
          }}
        />
      ))}
      <div
        className="absolute left-1/2 top-1/2 rounded-full"
        style={{
          width: '16%', aspectRatio: '1 / 1', transform: 'translate(-50%, -50%)',
          background: 'hsl(var(--gold) / 0.9)', boxShadow: '0 0 8px hsl(var(--gold) / 0.6)',
        }}
      />
    </div>
  </div>
);

const LidReveal = ({ box, art, scrim = 0.34, hinge = 'left', testid }) => {
  const [x0, x1, y0, y1] = box;
  const lw = x1 - x0;
  const lh = y1 - y0;
  const [open, setOpen] = useState(false);

  // Mount closed, then swing on the next frame so the transition runs.
  useEffect(() => {
    const id = requestAnimationFrame(() => requestAnimationFrame(() => setOpen(true)));
    return () => cancelAnimationFrame(id);
  }, []);

  const place = {
    left: `${pct(x0)}%`,
    top: `${pct(y0)}%`,
    width: `${pct(lw)}%`,
    height: `${pct(lh)}%`,
  };
  const sign = hinge === 'left' ? -1 : 1;

  return (
    <>
      {/* The recess the painting was hiding. */}
      <div
        className="absolute overflow-hidden rounded-sm"
        style={{
          ...place,
          background: 'linear-gradient(180deg, #050606 0%, #0b0c0d 100%)',
          boxShadow: 'inset 0 0 24px 6px #000',
        }}
        data-testid={`${testid}-recess`}
        aria-hidden="true"
      >
        <VaultDoor />
      </div>

      {/* The painting itself, as a lid. */}
      <div
        className="absolute"
        style={{
          ...place,
          // The same picture and scrim as the wall, cropped to the painting.
          backgroundImage:
            `linear-gradient(rgba(7,9,10,${scrim}), rgba(7,9,10,${scrim})), url(${art})`,
          backgroundSize: `100% 100%, ${pct(1 / lw)}% ${pct(1 / lh)}%`,
          backgroundPosition: `0 0, ${pct(x0 / (1 - lw))}% ${pct(y0 / (1 - lh))}%`,
          backgroundRepeat: 'no-repeat',
          transformOrigin: hinge === 'left' ? 'left center' : 'right center',
          transform: open
            ? `perspective(900px) rotateY(${sign * 78}deg)`
            : 'perspective(900px) rotateY(0deg)',
          transition: `transform ${LID_SWING_MS}ms cubic-bezier(0.25, 0.7, 0.2, 1), box-shadow ${LID_SWING_MS}ms ease-out`,
          boxShadow: open ? `${-sign * 18}px 10px 28px rgba(0,0,0,0.75)` : '0 0 0 rgba(0,0,0,0)',
        }}
        data-testid={`${testid}-lid`}
        aria-hidden="true"
      />
    </>
  );
};

export default LidReveal;
